import i18n from '@/config/i18n.config'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import CoverageBasic from '@/modules/common/common/models/APP/CoverageBasic'
import type FormPage from '@/modules/common/common/models/APP/FormPage'
import type DXPProduct from '@/modules/common/common/models/DXP/Product'
import ProductId from '@/modules/common/common/models/DXP/ProductId'
import GetProductResponse from '@/modules/common/common/models/DXP/responses/GetProductResponse'
import type RaterPricing from '@/modules/common/common/models/MID/RaterPricing'
import type BaseHiringStore from '@/modules/common/hiring/models/APP/BaseHiringStore'
import Summary from '@/modules/common/hiring/models/APP/Summary'
import GetActivityTypeResponse from '@/modules/common/hiring/models/MID/responses/GetActivityTypeResponse'
import GetProfessionTypeResponse from '@/modules/common/hiring/models/MID/responses/GetProfessionTypeResponse'
import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import { useNavigation } from '@/modules/core/composables/navigationComposable'
import { usePagesBuilder } from '@/modules/core/composables/usePagesBuilderComposable'
import ENDPOINTS from '@/modules/core/constants/apiEndpoints'
import {
  ErrorDisplayTypeEnum,
  InsuranceProcessStatusEnum,
  InsuranceState,
  UserRoleEnum
} from '@/modules/core/models/enums'
import CustomError from '@/modules/core/models/errors/CustomError'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import Message from '@/modules/core/models/modalMessage/Message'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import type { OptionsValuesInterface } from '@/modules/core/models/types'
import DXPService from '@/modules/core/services/DXPService'
import MiddlewareService from '@/modules/core/services/MiddlewareService'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useMessageStore } from '@/modules/core/stores/messageStore'
import { useToastStore } from '@/modules/core/stores/toastStore'
import { checkRecoveredQuotationBranch } from '@/modules/core/utils/BranchUtils'
import LifeCommonMapper from '@/modules/life/common/mappers'
import PAGES from '@/modules/life/hiring/constants/pages'
import LifeMapper from '@/modules/life/hiring/mappers'
import LifeInsuranceProcessStepEnum from '@/modules/life/hiring/models/APP/LifeInsuranceProcessStepEnum'
import router from '@/router'
import LifeConfigurations from '../../common/models/APP/LifeConfigurations'
import type LifeInsurance from '../../common/models/APP/LifeInsurance'
import LifeHiringAnswers from '../models/APP/LifeHiringAnswers'
import LifeHiringInsured from '../models/APP/LifeHiringInsured'
import LifeHiringSummary from '../models/APP/LifeHiringSummary'
import LifeGetIncrementRiskResponse from '../models/MID/responses/LifeGetIncrementRiskResponse'

export const useLifeStore = defineStore('life', () => {
  const errorManager = useErrorManagerComposable()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()
  const commonStore = useCommonStore()
  const pagesBuilder = usePagesBuilder()
  const navigation = useNavigation()
  const messageStore = useMessageStore()

  const { t } = i18n.global

  const globalConfig = ref(new LifeConfigurations())
  const hiringAnswers = ref(new LifeHiringAnswers())

  const professions = ref<OptionsValuesInterface[]>([])
  const activities = ref<OptionsValuesInterface[]>([])
  const coverages = ref<CoverageBasic[]>([])
  const bankEntities = ref<string[]>([])

  const insuredsState = computed(() => {
    const insureds = hiringAnswers.value.insureds
    const insuredsComplete = insureds.map(
      (i: any) => !!i.documentDetails.documentNumber || i.documentDetails.documentType === 'MENOR'
    )
    const holder = hiringAnswers.value.holder.role
    const index = holder === UserRoleEnum.HOLDER_NOT_INSURED ? 0 : 1

    return insuredsComplete.slice(index, insureds.length)
  })

  function getStepsBeforeSaveAndExit() {
    const stepCode = hiringAnswers.value.processStatus?.stepCode ?? 0
    const insureds = hiringAnswers.value.insureds as LifeHiringInsured[]
    const role = hiringAnswers.value.holder.role
    const insuranceStep = {
      label: t('copy_save-and-exit-modal_step-insurance'),
      check: true
    }

    const documentStep = {
      label: t('copy_save-and-exit-modal_step-document'),
      check: stepCode > LifeInsuranceProcessStepEnum.DOCUMENTS
    }

    const holderInsuredIndex = !insuredsState.value.length ? null : 1
    const holderStep = {
      label: t(`copy_save-and-exit-modal_step-holder-${role}`, { n: holderInsuredIndex }),
      check: stepCode > LifeInsuranceProcessStepEnum.HOLDER
    }

    const questionnaireStep = {
      label: t('copy_save-and-exit-modal_step-questionnaire'),
      check: !!(
        stepCode > LifeInsuranceProcessStepEnum.QUESTIONNAIRES || insureds[0].questionnaire.filled
      ),
      noOverride: true
    }

    const beneficiariesStep = {
      label: t('copy_save-and-exit-modal_step-beneficiaries'),
      check:
        stepCode > LifeInsuranceProcessStepEnum.BENEFICIARIES ||
        insureds[0].beneficiaries.length > 0,
      noOverride: true
    }

    return [insuranceStep, documentStep, holderStep, questionnaireStep, beneficiariesStep]
  }

  function isEmitted() {
    const stepCode = hiringAnswers.value?.processStatus?.stepCode
    if (!stepCode) throw new Error('Processs step code is required')

    return stepCode === LifeInsuranceProcessStepEnum.EMISSION
  }

  function isFinished() {
    const stepCode = hiringAnswers.value?.processStatus?.stepCode
    if (!stepCode) throw new Error('Processs step code is required')

    return stepCode > LifeInsuranceProcessStepEnum.PAYMENT
  }

  function getRePricingPages() {
    return PAGES.STEP0_PAGES
  }

  function getFirstPage(pages: Record<string, FormPage>) {
    if (isProcessExpired()) {
      return 'Step0_1_StartDateForm'
    }

    const insureds = hiringAnswers.value.insureds as LifeHiringInsured[]
    const processStep = hiringAnswers.value.processStatus?.stepCode ?? null

    return navigation.getPageByStepProcess<LifeHiringInsured>(
      pages,
      processStep,
      insureds,
      LifeInsuranceProcessStepEnum.INSUREDS
    )
  }

  function loadPages() {
    const expectedPages: Record<string, FormPage> = {}

    const holder = hiringAnswers.value.holder as LifeHiringInsured
    const insureds = hiringAnswers.value.insureds as LifeHiringInsured[]
    const insuredsToFill = pagesBuilder.getPageInsureds<LifeHiringInsured>(holder, insureds)
    if (holder.role === UserRoleEnum.HOLDER_NOT_INSURED) {
      Object.assign(expectedPages, PAGES.CALL_ME_PAGES)
      return expectedPages
    }

    const step1Pages = PAGES.STEP1_PAGES
    const step2Pages = pagesBuilder.getDynamicPages<LifeHiringInsured>(
      PAGES.STEP2_PAGES,
      insuredsToFill
    )

    // Join step1 with step2
    if (Object.keys(step1Pages).length) {
      const step1LastPage = pagesBuilder.getLastPageName(step1Pages)
      const step2FirstPage = pagesBuilder.getFirstPageName(step2Pages)
      step1Pages[step1LastPage].next[0] = step2FirstPage
    }

    // Join step2 with step3
    if (Object.keys(step2Pages).length) {
      const step2LastPage = pagesBuilder.getLastPageName(step2Pages)
      const step3FirstPage = pagesBuilder.getFirstPageName(PAGES.STEP3_PAGES)
      step2Pages[step2LastPage].next[0] = step3FirstPage
    }

    if (isProcessExpired()) {
      Object.assign(expectedPages, PAGES.STEP0_PAGES)
    }

    Object.assign(expectedPages, step1Pages)
    Object.assign(expectedPages, step2Pages)

    const step3Pages = PAGES.STEP3_PAGES
    const step4Pages = PAGES.STEP4_PAGES
    if (!hiringAnswers.value.insuranceTypes?.includes(4)) {
      delete step4Pages.Step4_1_HasMortgageForm

      // Join step3 with step4
      const step3LastPage = pagesBuilder.getLastPageName(step3Pages)
      const step4FirstPage = pagesBuilder.getFirstPageName(step4Pages)
      step3Pages[step3LastPage].next[0] = step4FirstPage
    }
    Object.assign(expectedPages, step3Pages)
    Object.assign(expectedPages, step4Pages)

    Object.assign(expectedPages, PAGES.STEP5_PAGES)

    return expectedPages
  }

  function getBranchConfiguration() {
    return globalConfig.value
  }

  function getSummary(): Summary {
    //loadCoverages
    const answers = hiringAnswers.value as LifeHiringAnswers
    return new LifeHiringSummary(
      answers,
      t,
      globalConfig.value,
      coverages.value,
      professions.value,
      activities.value
    )
  }

  function setCurrentInsuredAnswers(answers: Partial<LifeHiringInsured>, insuredIndex: number) {
    if (insuredIndex === 0) {
      hiringAnswers.value.updateHolderAttributes(answers)
    } else {
      hiringAnswers.value.updateInsuredAttributes(insuredIndex, answers)
    }
  }

  function setGloballyAnswers(answers: Partial<LifeHiringAnswers>) {
    hiringAnswers.value.updateAttributes(answers)
  }

  function _getCurrentInsured(insuredIndex?: number | string) {
    if (insuredIndex === undefined) return null

    if (insuredIndex === 0 || insuredIndex === 'holder') return hiringAnswers.value.holder

    if (hiringAnswers.value.holder.role === UserRoleEnum.HOLDER_NOT_INSURED) {
      return hiringAnswers.value.insureds[(insuredIndex as number) - 1]
    }

    return hiringAnswers.value.insureds[insuredIndex as number]
  }

  function getHiringAnswers(insuredIndex: number | string = 0): LifeHiringAnswers {
    const currentInsured = _getCurrentInsured(insuredIndex)
    const insured = currentInsured as LifeHiringInsured
    const answers = hiringAnswers.value as LifeHiringAnswers
    answers.currentInsured = insured ? new LifeHiringInsured(insured) : null

    return answers
  }

  //** PRIVATE SUPPORT METHODS */
  async function _fetchDXPInsurances(products: ProductId[]): Promise<DXPProduct[]> {
    const endpoint = ENDPOINTS.DXP_GET_PRODUCT
    const request = LifeCommonMapper.GetProductsMapper.toRequest(products)
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data.map((d: any) => new GetProductResponse(d).product)
  }

  function _loadCompleteSheetProducts(
    DXPProducts: DXPProduct[],
    MIDProducts: RaterPricing[]
  ): LifeInsurance[] {
    const products: LifeInsurance[] = []
    DXPProducts.forEach((DXPProduct) => {
      const MIDProduct = MIDProducts.find((p) => {
        return String(DXPProduct.productId) === String(p.productCode)
      })

      if (MIDProduct) {
        const insurance = LifeCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)
        products.push(insurance)
      }
    })

    return products
  }

  async function loadConfiguration(reset = false) {
    await loadConfigurationInner(reset)
    await loadReevaluationOptions()
  }

  async function loadReevaluationOptions() {
    try {
      if (
        globalConfig.value.reevaluationOptions &&
        globalConfig.value.reevaluationOptions?.length > 0
      )
        return
      const endpoint = ENDPOINTS.GET_REEVALUATION_OPTIONS
      const { error, data } = await MiddlewareService.post(endpoint, {
        BusinessLine: globalConfig.value.businessLineId,
        ClientCode: '24568',
        ProductCode: '408017'
      })
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }
      globalConfig.value.setReevaluationOptions(data)
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function loadConfigurationInner(reset = false) {
    try {
      if (globalConfig.value?.businessLineId && !reset) {
        hiringAnswers.value.businessLine = globalConfig.value.businessLineId
        return
      }
      const endpoint = ENDPOINTS.DXP_GET_BRANCH_LIFE
      const { error, data } = await DXPService.get(endpoint)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      globalConfig.value = new LifeConfigurations(data)
      hiringAnswers.value.businessLine = globalConfig.value.businessLineId
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function getNewRaterPrice(
    answers: LifeHiringAnswers,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST,
    isQuote: boolean = true,
    productCode: string | null | undefined = undefined
  ): Promise<LifeInsurance | null> {
    try {
      loadingStore.startLoading('common.loading.processing')

      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE_LIFE
      const request = LifeMapper.GetPricingMapper.toRequest(
        answers,
        isQuote,
        commonStore.infoMediator?.id
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)
      const products = data.listPricing.map((l: RaterPricing) => {
        return new ProductId({
          productCode: l.productCode,
          clientCode: l.clientCode
        })
      })
      const dxpData = await _fetchDXPInsurances(products)
      const insurances = _loadCompleteSheetProducts(dxpData, data.listPricing)
      return (
        (productCode ? insurances.find((i) => i.productCode === productCode) : insurances[0]) ??
        null
      )
    } catch (error) {
      if (error instanceof CustomError) {
        error.setDisplayType(errorDisplayType)
      }
      errorManager.handleError(error)

      return null
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function hireProduct(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: LifeInsurance,
    role: UserRoleEnum
  ) {
    let valid = true

    try {
      loadingStore.startLoading('Procesando...')

      if (!quotationId) throw new Error('Quotation id is required')

      const endpoint = ENDPOINTS.SAVE_QUOTATION_LIFE
      const request = LifeMapper.SaveQuotationMapper.toRequest(
        quotationId,
        startDate,
        billingCycle,
        product,
        role
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)
    } catch (error) {
      console.error(error)
      valid = false
    } finally {
      loadingStore.stopLoading()
    }

    return { valid }
  }

  async function loadCoverages(): Promise<CoverageBasic[]> {
    if (!hiringAnswers.value.insuranceTypes?.length || coverages.value.length) {
      return coverages.value
    }

    const { businessLineId, defaultInsuranceType } = globalConfig.value
    const insuranceType = hiringAnswers.value.insuranceTypes[0] ?? defaultInsuranceType
    coverages.value = await commonStore.loadCoverages({
      businessLine: businessLineId,
      insuranceType: String(insuranceType)!
    })

    return coverages.value
  }

  async function loadResources() {
    await Promise.allSettled([fetchProfessions(), fetchActivities()])
  }

  async function fetchHiringFormAnswers(quotationId: string) {
    try {
      commonStore.showLogo = false
      const endpoint = `${ENDPOINTS.GET_RECOVERY_LIFE}?quotationId=${quotationId}`
      const { error, data } = await MiddlewareService.get(endpoint)
      if (error) {
        const modalButtons = [new ModalButton({ text: 'copy_button_emission_exit', navTo: 'exit' })]
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setButtons(modalButtons)
      }

      await commonStore.resolveMediatorFromRecovery(data.mediator?.mediator)

      await loadConfiguration(true)
      const branchConfig = getBranchConfiguration()
      const itsOk = await checkRecoveredQuotationBranch(
        data.hiring.businessLine,
        branchConfig.businessLineId,
        quotationId
      )
      if (!itsOk) return

      const isHolderNotInsured = data.policyHolder.role === UserRoleEnum.HOLDER_NOT_INSURED
      if (!isHolderNotInsured) {
        await loadResources()
      }

      const { productCode, clientCode } = data.hiring
      const product = new ProductId({ productCode, clientCode })
      const dxpData = await _fetchDXPInsurances([product])
      if (!dxpData.length) {
        const modalButtons = [new ModalButton({ text: 'copy_button_emission_exit', navTo: 'exit' })]
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setButtons(modalButtons)
      }

      const DXPProduct = dxpData[0]
      const MIDProduct = data.hiring
      const insurance = LifeCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)

      hiringAnswers.value = LifeMapper.GetRecoveryMapper.fromResponse(data, insurance)
      hiringAnswers.value.reevaluationType =
        globalConfig.value.reevaluationOptions?.find(
          (o) => o.code === data.hiring.revaluationType
        ) ?? null
      if (!isHolderNotInsured) {
        await loadCoverages()
      }
    } catch (error) {
      errorManager.handleError(error)
      loadingStore.stopLoading()
    } finally {
      commonStore.showLogo = true
    }
  }

  async function fetchProfessions(): Promise<OptionsValuesInterface[]> {
    try {
      if (professions.value.length) {
        return professions.value
      }

      const endpoint = `${ENDPOINTS.GET_PROFESSION}?businessLine=${hiringAnswers.value.businessLine}`
      const { error, data } = await MiddlewareService.get(endpoint)
      if (error) throw new ServiceError(data)

      const MIDProfessions = new GetProfessionTypeResponse(data).professions
      professions.value = MIDProfessions.map((p) => ({
        value: String(p.code),
        label: p.description
      }))

      return professions.value
    } catch (error) {
      errorManager.handleError(error)
      return []
    }
  }

  async function fetchActivities(): Promise<OptionsValuesInterface[]> {
    try {
      if (activities.value.length) {
        return activities.value
      }

      const endpoint = `${ENDPOINTS.GET_ACTIVITY}?businessLine=${hiringAnswers.value.businessLine}`
      const { error, data } = await MiddlewareService.get(endpoint)
      if (error) throw new ServiceError(data)

      const MIDActivities = new GetActivityTypeResponse(data).activities
      activities.value = MIDActivities.map((a) => ({
        value: String(a.code),
        label: a.description
      })).sort((a, b) => a.label.localeCompare(b.label))

      return activities.value
    } catch (error) {
      errorManager.handleError(error)
      return []
    }
  }

  async function getIncrementRisk(
    profession: number,
    activity: number
  ): Promise<LifeGetIncrementRiskResponse> {
    try {
      const endpoint = ENDPOINTS.GET_INCREMENT_RISK
      const request = {
        quotationId: hiringAnswers.value.quotationId,
        profession,
        activity
      }
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)

      return new LifeGetIncrementRiskResponse(data)
    } catch (error) {
      errorManager.handleError(error)
      return new LifeGetIncrementRiskResponse({
        priceRice: false,
        amountPriceRice: 0,
        allowsHiring: false
      })
    }
  }

  async function fetchEntities(): Promise<string[]> {
    try {
      if (bankEntities.value.length) {
        return bankEntities.value
      }

      const endpoint = ENDPOINTS.DXP_GET_BANK_ENTITIES
      const { error, data } = await DXPService.get(endpoint)
      if (error) throw new ServiceError(data)

      const entities: { id: string; name: string }[] = data
      bankEntities.value = entities.map(({ name }) => name).sort((a, b) => a.localeCompare(b))

      return bankEntities.value
    } catch (error) {
      errorManager.handleError(error)

      return []
    }
  }

  const availableHiringMethods = {
    sendPolicyHolderDocument,
    sendPolicyHolder,
    sendInsureds,
    sendQuestionnaire,
    sendBeneficiaries,
    sendPayment
  }
  async function callToHiringMethod(
    methodName: string,
    answers: LifeHiringAnswers,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST
  ) {
    try {
      loadingStore.startLoading('Procesando...')

      if (methodName && methodName in availableHiringMethods) {
        const key = methodName as keyof typeof availableHiringMethods
        await availableHiringMethods[key](answers)
      }
    } catch (error) {
      const err = error instanceof CustomError ? error.setDisplayType(errorDisplayType) : error
      errorManager.handleError(err)
      throw error
    }
  }
  function _setStepProcess(stepProcess: LifeInsuranceProcessStepEnum) {
    const currentStepCode = hiringAnswers.value.processStatus.stepCode ?? 0
    if (currentStepCode < stepProcess) {
      hiringAnswers.value.processStatus.stepCode = stepProcess
    }
  }
  async function sendPolicyHolderDocument(answers: LifeHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_DOCUMENT_LIFE
    const quotationId = answers.quotationId ?? ''
    const holder = answers.holder as LifeHiringInsured
    const request = await LifeMapper.SaveDocumentMapper.toRequest(quotationId, holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    _setStepProcess(LifeInsuranceProcessStepEnum.HOLDER)
    toastStore.successToast('health.saved-holder-data-and-document')
  }

  async function sendPolicyHolder(answers: LifeHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_HOLDER_LIFE
    const quotationId = answers.quotationId ?? ''
    const holder = answers.holder
    const request = LifeMapper.SavePolicyHolderMapper.toRequest(quotationId, holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const oneInsured = answers.insureds.length === 1
    const onlyInsureHolder = oneInsured && answers.holder.role === UserRoleEnum.HOLDER_INSURED
    const processStep = onlyInsureHolder
      ? LifeInsuranceProcessStepEnum.QUESTIONNAIRES
      : LifeInsuranceProcessStepEnum.INSUREDS
    _setStepProcess(processStep)

    toastStore.successToast(
      answers.holder.role === UserRoleEnum.HOLDER_INSURED
        ? 'health.saved-holder-and-others-data'
        : 'health.saved-holder-data'
    )
  }

  async function sendInsureds(answers: LifeHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_INSUREDS_LIFE
    const quotationId = answers.quotationId ?? ''

    if (answers.currentInsured?.role !== UserRoleEnum.INSURED) return

    const request = LifeMapper.SaveInsuredMapper.toRequest(quotationId, [answers.currentInsured])
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const isLastInsured =
      answers.insureds[answers.insureds.length - 1].riskNumber ===
      answers.currentInsured?.riskNumber
    if (isLastInsured) {
      _setStepProcess(LifeInsuranceProcessStepEnum.QUESTIONNAIRES)
    }
    toastStore.successToast(
      t('health.saved-insured-data', { riskNumber: answers.currentInsured?.riskNumber })
    )
  }

  async function sendQuestionnaire(answers: LifeHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_QUESTIONNAIRE_LIFE
    const quotationId = answers.quotationId ?? ''

    const insured = answers.insureds[0]
    const questionnaire = insured.questionnaire
    const request = LifeMapper.SaveQuestionnaireMapper.toRequest(
      quotationId,
      questionnaire,
      insured
    )
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    _setStepProcess(LifeInsuranceProcessStepEnum.BENEFICIARIES)

    toastStore.successToast('health.saved-holder-and-others-data')
  }

  async function sendBeneficiaries(answers: LifeHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_BENEFICIARY_LIFE
    const quotationId = answers.quotationId ?? ''

    const insured = answers.insureds[0]
    if (!insured.availableToSave) return

    const request = LifeMapper.SaveBeneficiariesMapper.toRequest(quotationId, insured)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    _setStepProcess(LifeInsuranceProcessStepEnum.PAYMENT)
    toastStore.successToast('copy_life_save-beneficiaries-service-message')
  }

  async function sendPayment(answers: LifeHiringAnswers) {
    const quotationId = answers.quotationId ?? ''
    const bankDetails = answers.bankDetails

    const { error, data } = await commonStore.sendPayment(
      quotationId,
      bankDetails,
      ENDPOINTS.SAVE_PAYMENT_LIFE
    )
    if (error) throw new ServiceError(data)

    const statusCode = data.codeStateEmission
    const { PENDING_EMISSION, PENDING, ISSUED } = InsuranceState
    if (ISSUED === statusCode) {
      const hash = btoa(quotationId)
      router.push({ name: 'life-hiring-done', params: { hash } })
      const modalMessage = new Message({
        title: t('copy_hiring-status_emitted-modal_title'),
        label: t('copy_hiring-status_emitted-modal_label'),
        buttons: [
          new ModalButton({
            text: 'copy_hiring-status_pending-modal_button',
            navTo: 'exit'
          })
        ]
      })
      messageStore.successModal(modalMessage)
    } else if ([PENDING_EMISSION, PENDING].includes(statusCode)) {
      const modalMessage = new Message({
        title: t('copy_hiring-status_pending-modal_title'),
        label: t('copy_hiring-status_pending-modal_label'),
        extra: {
          hiringNumber: answers?.product?.quotationNumber,
          mailReminderAfterEmission: t('components.insurance-process.info2')
        },
        buttons: [
          new ModalButton({
            text: 'copy_hiring-status_pending-modal_button',
            navTo: 'exit'
          })
        ]
      })
      messageStore.pendingModal(modalMessage)
    }
    const hash = btoa(quotationId)
    router.push({ name: 'life-hiring-done', params: { hash } })
    loadingStore.stopLoading()
  }

  async function emission(quotationId: string) {
    return await commonStore.fetchInsuranceState(quotationId, 'hiring-life')
  }

  function isProcessExpired(): boolean {
    const statusCode = hiringAnswers.value.processStatus.statusCode
    return statusCode === InsuranceProcessStatusEnum.EXPIRED
  }

  function getBranchName(): string {
    return 'life'
  }

  function checkIfNeedsRecalculate(): boolean {
    return false
  }

  function hasFinishedInsuredSteps(processStep: LifeInsuranceProcessStepEnum) {
    return processStep >= LifeInsuranceProcessStepEnum.INSUREDS
  }

  const store: BaseHiringStore<
    LifeInsurance,
    LifeHiringInsured,
    LifeHiringAnswers,
    LifeConfigurations
  > & {
    fetchProfessions(): Promise<OptionsValuesInterface[]>
    fetchActivities(): Promise<OptionsValuesInterface[]>
    getIncrementRisk(profession: number, activity: number): Promise<LifeGetIncrementRiskResponse>
    loadCoverages(): Promise<CoverageBasic[]>
    fetchEntities(): Promise<string[]>
  } = {
    isFinished,
    isEmitted,
    getStepsBeforeSaveAndExit,
    getRePricingPages,
    getFirstPage,
    getBranchConfiguration,
    getSummary,

    loadConfiguration,
    loadPages,

    setCurrentInsuredAnswers,
    setGloballyAnswers,
    getHiringAnswers,

    getNewRaterPrice,
    hireProduct,

    isProcessExpired,
    fetchHiringFormAnswers,
    callToHiringMethod,
    emission,
    getBranchName,
    hasFinishedInsuredSteps,

    // Custom methods
    fetchProfessions,
    fetchActivities,
    getIncrementRisk,
    loadCoverages,
    fetchEntities,

    // not implemented
    checkIfNeedsRecalculate
  }

  return store
})
